import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ShoppingBag, Package, Users, Mail, TrendingUp, Crown, Workflow, UserPlus, Sparkles } from "lucide-react";

const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-yellow-400 text-black",
    shipped: "bg-blue-500 text-white",
    delivered: "bg-green-500 text-black",
    cancelled: "bg-red-500 text-white",
  };
  return (
    <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-widest ${colors[status] || "bg-zinc-700 text-white"}`}>
      {status}
    </span>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [crm, setCrm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/crm/stats").then((r) => setCrm(r.data)).catch(() => setCrm(null)),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="font-heading text-2xl text-zinc-500 uppercase">Caricamento...</div>;
  if (!stats) return <div className="text-red-400">Errore caricamento stats</div>;

  const cards = [
    { label: "Ricavo Totale", value: `€ ${stats.total_revenue.toFixed(2)}`, icon: TrendingUp, accent: true },
    { label: "Ordini", value: stats.total_orders, icon: ShoppingBag },
    { label: "Prodotti", value: stats.total_products, icon: Package },
    { label: "Newsletter", value: stats.total_newsletter, icon: Mail },
    { label: "Admin", value: stats.total_admins, icon: Users },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">ダッシュボード</div>
        <h1 className="font-heading text-5xl uppercase text-white leading-none">Dashboard</h1>
        <p className="text-zinc-400 mt-2">Tutto il regno di Daita 'n' Jack in una vista.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`p-5 border-2 ${c.accent ? "bg-yellow-400 text-black border-yellow-400" : "bg-zinc-900 text-white border-zinc-800"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <Icon className="w-5 h-5" strokeWidth={3} />
                <span className={`text-[10px] uppercase tracking-widest ${c.accent ? "text-black/60" : "text-zinc-500"}`}>
                  {c.label}
                </span>
              </div>
              <div className="font-heading text-3xl">{c.value}</div>
            </div>
          );
        })}
      </div>

      {/* CRM Pulse */}
      {crm && (
        <div data-testid="crm-pulse" className="bg-zinc-900 border-2 border-zinc-800">
          <div className="flex items-center justify-between px-5 py-3 border-b-2 border-zinc-800 bg-zinc-950">
            <div className="flex items-center gap-2">
              <Workflow className="w-5 h-5 text-yellow-400" strokeWidth={3} />
              <h2 className="font-heading text-xl uppercase text-white">CRM Pulse</h2>
              <span className="font-jp text-yellow-400 text-[10px] tracking-widest ml-2">脈拍</span>
            </div>
            <Link
              to="/admin/crm/pipeline"
              data-testid="crm-pulse-link"
              className="text-yellow-400 hover:text-white text-xs font-heading uppercase tracking-widest border-2 border-yellow-400 px-3 py-1.5 hover:bg-yellow-400 hover:!text-black transition"
            >
              Vai alla Pipeline →
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-7 gap-px bg-zinc-800">
            <CrmCell label="Lead" value={crm.counts.lead} accent="text-zinc-300" subLabel="totali" stageLink="lead" />
            <CrmCell label="Club Pending" value={crm.counts.pending} accent="text-orange-300" stageLink="pending" />
            <CrmCell label="Clienti" value={crm.counts.client} accent="text-green-300" stageLink="client" />
            <CrmCell label="VIP Fedeltà" value={crm.counts.vip} accent="text-yellow-400" stageLink="vip" />
            <CrmCell
              label="Nuovi Lead"
              subLabel="ultimi 7gg"
              value={crm.new_leads_week}
              accent="text-yellow-400"
              icon={UserPlus}
            />
            <CrmCell
              label="Clienti Attivi"
              subLabel="con ordine"
              value={crm.active_clients}
              accent="text-yellow-400"
              icon={Crown}
            />
            <CrmCell
              label="Spesa Media VIP"
              value={`€ ${(crm.vip_avg_spend || 0).toFixed(0)}`}
              accent="text-yellow-400"
              icon={Sparkles}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border-2 border-zinc-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-yellow-400" strokeWidth={3} />
            <h2 className="font-heading text-xl uppercase text-white">Top Prodotti</h2>
          </div>
          {stats.top_products.length === 0 && <div className="text-zinc-500 text-sm">Ancora nessun ordine.</div>}
          <ul className="space-y-3">
            {stats.top_products.map((p, i) => (
              <li key={p.product_id} className="flex items-center justify-between border-b border-zinc-800 pb-2">
                <div className="flex items-center gap-3">
                  <span className="font-heading text-2xl text-yellow-400 w-8">#{i + 1}</span>
                  <span className="text-white font-bold">{p.title}</span>
                </div>
                <div className="text-right">
                  <div className="text-yellow-400 font-bold">€ {p.revenue.toFixed(2)}</div>
                  <div className="text-zinc-500 text-xs">{p.qty} venduti</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-zinc-900 border-2 border-zinc-800 p-6">
          <h2 className="font-heading text-xl uppercase text-white mb-4">Ultimi 5 Ordini</h2>
          {stats.recent_orders.length === 0 && <div className="text-zinc-500 text-sm">Nessun ordine ancora.</div>}
          <ul className="space-y-3">
            {stats.recent_orders.map((o) => (
              <li key={o.id} className="flex items-center justify-between border-b border-zinc-800 pb-2 gap-3">
                <div className="min-w-0">
                  <div className="text-white font-bold truncate">{o.customer_name}</div>
                  <div className="text-zinc-500 text-xs">#{o.id.slice(0, 8).toUpperCase()} · {o.customer_email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-yellow-400 font-bold">€ {o.total.toFixed(2)}</div>
                  <StatusBadge status={o.status} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CrmCell({ label, subLabel, value, accent = "text-white", icon: Icon, stageLink }) {
  const Wrapper = stageLink ? Link : "div";
  const props = stageLink
    ? {
        to: `/admin/crm/contacts?stage=${stageLink}`,
        "data-testid": `crm-cell-${stageLink}`,
        className: "block bg-zinc-900 p-4 hover:bg-zinc-950 transition",
      }
    : { className: "bg-zinc-900 p-4", "data-testid": `crm-cell-${label.toLowerCase().replace(/\s+/g, "-")}` };
  return (
    <Wrapper {...props}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-500" strokeWidth={3} />}
      </div>
      <div className={`font-heading text-2xl ${accent}`}>{value}</div>
      {subLabel && <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1">{subLabel}</div>}
    </Wrapper>
  );
}
